// PartnershipDetail — full-page view at `/syndication/partnerships/:id`.
//
// Reuses the heavy lifting of SyndicationTab (cap table, sponsor actions)
// by simply showing the same component inside an AppShell. This keeps the
// "view from outside a deal" entry point usable for LPs who land here via
// the invite-accept redirect.

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import { useAuth } from "@/contexts/AuthContext";
import TabErrorBoundary from "@/components/TabErrorBoundary";
import DistributionsSection from "@/components/deal/DistributionsSection";
import InvestorLettersSection from "@/components/syndication/InvestorLettersSection";
import CapTablePieChart from "@/components/syndication/CapTablePieChart";
import DistributionSparkline from "@/components/syndication/DistributionSparkline";

function SponsorEditStakeModal({ open, onClose, partnershipId, current, onSaved }) {
  const [pct, setPct] = useState(current?.ownership_pct ?? 0);
  const [committed, setCommitted] = useState(current?.committed ?? 0);
  const [displayName, setDisplayName] = useState(current?.display_name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (current) {
      setPct(current.ownership_pct ?? 0);
      setCommitted(current.committed ?? 0);
      setDisplayName(current.display_name || "");
    }
  }, [current]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      if (Number(pct) !== Number(current.ownership_pct)) body.ownership_pct = Number(pct);
      if (Number(committed) !== Number(current.committed)) body.committed_amount = Number(committed);
      if ((displayName || "").trim() !== (current.display_name || "")) body.display_name = displayName.trim();
      if (Object.keys(body).length === 0) {
        toast.message("No changes to save");
        onClose();
        return;
      }
      await api.patch(`/syndication/partnerships/${partnershipId}/members/me`, body);
      toast.success("Your stake was updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update stake");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="synd-sponsor-edit-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit my stake</DialogTitle>
          <DialogDescription>
            Update your ownership %, contributed capital, and display name.
            Locked once the partnership is funded.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="sp-pct">Ownership %</Label>
            <Input
              id="sp-pct" type="number" min="0" max="100" step="0.01"
              data-testid="synd-sponsor-edit-pct-input"
              value={pct} onChange={(e) => setPct(e.target.value)} required
            />
          </div>
          <div>
            <Label htmlFor="sp-committed">Committed capital (USD)</Label>
            <Input
              id="sp-committed" type="number" min="0"
              data-testid="synd-sponsor-edit-committed-input"
              value={committed} onChange={(e) => setCommitted(e.target.value)} required
            />
          </div>
          <div>
            <Label htmlFor="sp-name">Display name</Label>
            <Input
              id="sp-name" data-testid="synd-sponsor-edit-display-name-input"
              value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit" disabled={busy}
              data-testid="synd-sponsor-edit-submit-btn"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pencil className="mr-2 h-4 w-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnershipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fmt = useFmt();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStakeEdit, setShowStakeEdit] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Phase 34c.3 chart-wiring fix — pull cap-table + distribution runs
      // in parallel so the pie chart + sparkline render on first paint
      // here too (not just inside the Deal-Detail Syndication tab).
      const [capResp, runsResp] = await Promise.all([
        api.get(`/syndication/partnerships/${id}/cap-table`),
        api.get(`/syndication/partnerships/${id}/distributions`).catch(() => ({ data: [] })),
      ]);
      setData(capResp.data);
      setRuns(runsResp.data || []);
    } catch {
      setData(null);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <h1 className="text-xl font-semibold text-slate-900">
            Partnership not found
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            It may have been deleted, or you may not have access.
          </p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-indigo-600 text-white hover:bg-indigo-700">
            Back to dashboard
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-indigo-600"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{data.partnership.name}</h1>
          <p className="text-sm text-slate-500">
            Status <span className="font-medium uppercase tracking-wider text-slate-700">{data.partnership.status}</span>
            {" · "}Target {fmt.money(data.totals.target)}
          </p>
          {(() => {
            const isSponsor = !!user && data.partnership.sponsor_user_id === user.id;
            const editable = isSponsor && ["draft", "open"].includes(data.partnership.status);
            return editable ? (
              <Button
                onClick={() => setShowStakeEdit(true)}
                data-testid="synd-sponsor-edit-stake-btn"
                variant="outline"
                className="mt-3 border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="mr-2 h-3.5 w-3.5" /> Edit my stake
              </Button>
            ) : null;
          })()}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <TabErrorBoundary tabName="Cap table">
            <div className="lg:col-span-2">
              <CapTablePieChart members={data.members} />
            </div>
          </TabErrorBoundary>
          <TabErrorBoundary tabName="Distribution history">
            <DistributionSparkline runs={runs} />
          </TabErrorBoundary>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-right">Ownership %</th>
                <th className="px-4 py-3 text-right">Committed</th>
                <th className="px-4 py-3 text-right">Contributed</th>
                <th className="px-4 py-3 text-right">Pref Paid</th>
                <th className="px-4 py-3 text-right">Capital Returned</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.members.map((m) => (
                <tr key={m.member_id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{m.display_name || m.email}</div>
                    {m.display_name ? <div className="text-xs text-slate-500">{m.email}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{m.role}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{m.ownership_pct.toFixed(2)}%</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.committed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.contributed_total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.cumulative_pref_paid || 0)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.cumulative_capital_returned || 0)}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{m.invite_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TabErrorBoundary tabName="Distributions">
          <DistributionsSection
            partnership={data.partnership}
            isSponsor={!!user && data.partnership.sponsor_user_id === user.id}
          />
        </TabErrorBoundary>
        {/* Phase 35e — Investor letter generator (sponsor only) */}
        {!!user && data.partnership.sponsor_user_id === user.id && (
          <TabErrorBoundary tabName="Investor letters">
            <InvestorLettersSection partnershipId={data.partnership.id} />
          </TabErrorBoundary>
        )}
      </div>
      <SponsorEditStakeModal
        open={showStakeEdit}
        onClose={() => setShowStakeEdit(false)}
        partnershipId={data.partnership.id}
        current={data.members.find((m) => m.role === "sponsor")}
        onSaved={refresh}
      />
    </AppShell>
  );
}
